import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarDays, Search, CheckCircle, XCircle, Loader2, RotateCcw, Camera, Trash2, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminBookings = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, booking: null, action: null });
  const [refundLoading, setRefundLoading] = useState(null);
  const [photoDialog, setPhotoDialog] = useState({ open: false, booking: null });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoMessage, setPhotoMessage] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [invoiceDeleteLoading, setInvoiceDeleteLoading] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, booking: null });
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [deleteBookingLoading, setDeleteBookingLoading] = useState(null);
  const [locations, setLocations] = useState([]);
  const [trailers, setTrailers] = useState([]);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      const url = statusFilter === 'all'
        ? `${API_URL}/api/admin/bookings`
        : `${API_URL}/api/admin/bookings?status=${statusFilter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const confirmBooking = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/confirm`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, booking: null, action: null });
    }
  };

  const rejectBooking = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      }
    } catch (error) {
      console.error('Error rejecting booking:', error);
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, booking: null, action: null });
    }
  };

  const refundBooking = async (bookingId) => {
    setRefundLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/refund`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      }
    } catch (error) {
      console.error('Error refunding booking:', error);
    } finally {
      setRefundLoading(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const deleteInvoice = async (bookingId) => {
    if (!window.confirm('Rechnung wirklich löschen?')) return;
    setInvoiceDeleteLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/invoice`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Löschen der Rechnung');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
    } finally {
      setInvoiceDeleteLoading(null);
    }
  };

  const openEditDialog = (booking) => {
    setEditForm({
      start_date: booking.start_date?.split('T')[0] || '',
      end_date: booking.end_date?.split('T')[0] || '',
      total_price: booking.total_price || 0,
      base_price: booking.base_price || 0,
      customer_name: booking.customer_name || '',
      customer_email: booking.customer_email || '',
      customer_phone: booking.customer_phone || '',
      notes: booking.notes || '',
      location_id: booking.location_id || '',
      trailer_id: booking.trailer_id || '',
    });
    // Fetch locations and trailers for dropdowns
    fetchLocationsAndTrailers();
    setEditDialog({ open: true, booking });
  };

  const fetchLocationsAndTrailers = async () => {
    try {
      const [locsRes, trailersRes] = await Promise.all([
        fetch(`${API_URL}/api/locations`),
        fetch(`${API_URL}/api/admin/trailers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (locsRes.ok) setLocations(await locsRes.json());
      if (trailersRes.ok) setTrailers(await trailersRes.json());
    } catch (_) {}
  };

  const saveBookingEdit = async () => {
    if (!editDialog.booking) return;
    setEditLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${editDialog.booking.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        fetchBookings();
        setEditDialog({ open: false, booking: null });
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error editing booking:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Buchung wirklich endgültig löschen? Dies kann nicht rückgängig gemacht werden.')) return;
    setDeleteBookingLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    } finally {
      setDeleteBookingLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      paid: { label: 'Bezahlt', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      confirmed: { label: 'Bestätigt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Abgelehnt', className: 'bg-red-50 text-red-700 border-red-200' },
      cancelled: { label: 'Storniert', className: 'bg-slate-50 text-slate-700 border-slate-200' },
      expired: { label: 'Abgelaufen', className: 'bg-slate-50 text-slate-500 border-slate-200' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const uploadCompletionPhoto = async () => {
    if (!photoFile || !photoDialog.booking) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('message', photoMessage);
      const response = await fetch(`${API_URL}/api/admin/bookings/${photoDialog.booking.id}/completion-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        fetchBookings();
        setPhotoDialog({ open: false, booking: null });
        setPhotoFile(null);
        setPhotoMessage('');
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading completion photo:', error);
    } finally {
      setPhotoUploading(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.customer_name?.toLowerCase().includes(search) ||
      booking.customer_email?.toLowerCase().includes(search) ||
      booking.location?.name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Confirm/Reject Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, booking: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === 'confirm' ? 'Buchung bestätigen' : 'Buchung ablehnen'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'confirm'
                ? `Möchten Sie die Buchung von "${confirmDialog.booking?.customer_name}" für "${confirmDialog.booking?.location?.name}" bestätigen? Der Kunde wird per E-Mail benachrichtigt.`
                : `Möchten Sie die Buchung von "${confirmDialog.booking?.customer_name}" für "${confirmDialog.booking?.location?.name}" ablehnen? Der Kunde wird per E-Mail benachrichtigt.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, booking: null, action: null })}>
              Abbrechen
            </Button>
            {confirmDialog.action === 'confirm' ? (
              <Button
                onClick={() => confirmBooking(confirmDialog.booking?.id)}
                disabled={actionLoading === confirmDialog.booking?.id}
                data-testid="confirm-booking-dialog-button"
              >
                {actionLoading === confirmDialog.booking?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Bestätigen
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => rejectBooking(confirmDialog.booking?.id)}
                disabled={actionLoading === confirmDialog.booking?.id}
                data-testid="reject-booking-dialog-button"
              >
                {actionLoading === confirmDialog.booking?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Ablehnen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">{t('admin.bookings')}</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{bookings.length} Buchungen</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Input
            placeholder="Suchen nach Name, E-Mail oder Standort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="bookings-search-input"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">Alle</TabsTrigger>
            <TabsTrigger value="pending">Ausstehend</TabsTrigger>
            <TabsTrigger value="paid">Bezahlt</TabsTrigger>
            <TabsTrigger value="confirmed">Bestätigt</TabsTrigger>
            <TabsTrigger value="rejected">Abgelehnt</TabsTrigger>
            <TabsTrigger value="cancelled">Storniert</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <CalendarDays className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Buchungen</h3>
            <p className="text-[hsl(var(--muted-foreground))]">
              {searchTerm ? 'Keine Buchungen gefunden für diese Suche.' : 'Es gibt noch keine Buchungen.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-shadow">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Kunde</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Standort</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Zeitraum</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Betrag</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Erstellt</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking, index) => (
                    <motion.tr
                      key={booking.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.5)] transition-colors"
                    >
                      <td className="py-4 px-4">
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{booking.customer_email}</p>
                        {booking.customer_phone && (
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">{booking.customer_phone}</p>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-medium">{booking.location?.name || '-'}</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{booking.location?.address}</p>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-sm">
                          {format(new Date(booking.start_date), 'dd.MM.yyyy', { locale: de })}
                        </p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">bis</p>
                        <p className="text-sm">
                          {format(new Date(booking.end_date), 'dd.MM.yyyy', { locale: de })}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-semibold">CHF {formatPrice(booking.total_price)}</p>
                        {booking.invoice_number && (
                          <div className="flex items-center gap-1 mt-1">
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">
                              {booking.invoice_number}
                            </p>
                            <button
                              onClick={() => deleteInvoice(booking.id)}
                              disabled={invoiceDeleteLoading === booking.id}
                              className="text-red-400 hover:text-red-600 transition-colors p-0.5"
                              title="Rechnung löschen"
                              data-testid={`delete-invoice-${booking.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(booking.status)}
                        {booking.paid_at && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                            Bezahlt: {format(new Date(booking.paid_at), 'dd.MM.yy HH:mm', { locale: de })}
                          </p>
                        )}
                        {booking.confirmed_at && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            Bestätigt: {format(new Date(booking.confirmed_at), 'dd.MM.yy HH:mm', { locale: de })}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-[hsl(var(--muted-foreground))]">
                        {format(new Date(booking.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-2">
                          {/* Confirm/Reject buttons for PAID bookings */}
                          {booking.status === 'paid' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => setConfirmDialog({ open: true, booking, action: 'confirm' })}
                                disabled={actionLoading === booking.id}
                                data-testid={`confirm-booking-${booking.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Bestätigen
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-8 text-xs"
                                onClick={() => setConfirmDialog({ open: true, booking, action: 'reject' })}
                                disabled={actionLoading === booking.id}
                                data-testid={`reject-booking-${booking.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Ablehnen
                              </Button>
                            </div>
                          )}
                          {/* Refund button for paid/cancelled/rejected (not yet refunded) */}
                          {['paid', 'cancelled', 'rejected'].includes(booking.status) && !booking.refunded && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => refundBooking(booking.id)}
                              disabled={refundLoading === booking.id}
                              data-testid={`refund-booking-${booking.id}`}
                            >
                              {refundLoading === booking.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                              Erstatten
                            </Button>
                          )}
                          {/* Completion photo button for confirmed bookings */}
                          {booking.status === 'confirmed' && !booking.completion_photo_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => setPhotoDialog({ open: true, booking })}
                              data-testid={`completion-photo-${booking.id}`}
                            >
                              <Camera className="w-3 h-3 mr-1" />
                              Foto hochladen
                            </Button>
                          )}
                          {booking.completion_photo_url && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                              <Camera className="w-3 h-3 mr-1" />
                              Foto vorhanden
                            </Badge>
                          )}
                          {booking.refunded && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              Erstattet
                            </Badge>
                          )}
                          {/* General status dropdown */}
                          <Select
                            value={booking.status}
                            onValueChange={(value) => updateStatus(booking.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`booking-status-select-${booking.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Ausstehend</SelectItem>
                              <SelectItem value="paid">Bezahlt</SelectItem>
                              <SelectItem value="confirmed">Bestätigt</SelectItem>
                              <SelectItem value="rejected">Abgelehnt</SelectItem>
                              <SelectItem value="cancelled">Storniert</SelectItem>
                              <SelectItem value="expired">Abgelaufen</SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Edit & Delete */}
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs"
                              onClick={() => openEditDialog(booking)}
                              data-testid={`edit-booking-${booking.id}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => deleteBooking(booking.id)}
                              disabled={deleteBookingLoading === booking.id}
                              data-testid={`delete-booking-${booking.id}`}
                            >
                              {deleteBookingLoading === booking.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                              Löschen
                            </Button>
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Completion Photo Dialog */}
      <Dialog open={photoDialog.open} onOpenChange={(open) => { if (!open) { setPhotoDialog({ open: false, booking: null }); setPhotoFile(null); setPhotoMessage(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-emerald-600" />
              Auftragsbestätigung mit Foto
            </DialogTitle>
            <DialogDescription>
              Laden Sie ein Foto des montierten Plakats hoch. Der Kunde erhält eine E-Mail mit dem Foto und Dankeschön.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium block mb-2">Foto auswählen *</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setPhotoFile(e.target.files[0])}
                className="block w-full text-sm text-[hsl(var(--muted-foreground))] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[hsl(var(--primary))] file:text-white hover:file:opacity-90"
                data-testid="completion-photo-input"
              />
              {photoFile && (
                <p className="text-xs text-emerald-600 mt-2">{photoFile.name} ausgewählt</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Nachricht (optional)</label>
              <Input
                placeholder="z.B. Plakat erfolgreich montiert am..."
                value={photoMessage}
                onChange={(e) => setPhotoMessage(e.target.value)}
                data-testid="completion-photo-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPhotoDialog({ open: false, booking: null }); setPhotoFile(null); setPhotoMessage(''); }}>
              Abbrechen
            </Button>
            <Button onClick={uploadCompletionPhoto} disabled={!photoFile || photoUploading} data-testid="confirm-completion-photo-button">
              {photoUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              Foto senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => { if (!open) setEditDialog({ open: false, booking: null }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[hsl(var(--primary))]" />
              Buchung bearbeiten
            </DialogTitle>
            <DialogDescription>
              Buchung von {editDialog.booking?.customer_name} — {editDialog.booking?.location?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Startdatum</label>
                <Input type="date" value={editForm.start_date || ''} onChange={(e) => setEditForm({...editForm, start_date: e.target.value})} data-testid="edit-start-date" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Enddatum</label>
                <Input type="date" value={editForm.end_date || ''} onChange={(e) => setEditForm({...editForm, end_date: e.target.value})} data-testid="edit-end-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Standort</label>
                <Select value={editForm.location_id || ''} onValueChange={(v) => setEditForm({...editForm, location_id: v})}>
                  <SelectTrigger data-testid="edit-location"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Wagen/Anhaenger</label>
                <Select value={editForm.trailer_id || 'none'} onValueChange={(v) => setEditForm({...editForm, trailer_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-trailer"><SelectValue placeholder="Wagen wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Wagen</SelectItem>
                    {trailers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Grundpreis (CHF)</label>
                <Input type="number" step="0.01" value={editForm.base_price || ''} onChange={(e) => setEditForm({...editForm, base_price: parseFloat(e.target.value) || 0})} data-testid="edit-base-price" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Gesamtpreis (CHF)</label>
                <Input type="number" step="0.01" value={editForm.total_price || ''} onChange={(e) => setEditForm({...editForm, total_price: parseFloat(e.target.value) || 0})} data-testid="edit-total-price" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Kundenname</label>
              <Input value={editForm.customer_name || ''} onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})} data-testid="edit-customer-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">E-Mail</label>
                <Input value={editForm.customer_email || ''} onChange={(e) => setEditForm({...editForm, customer_email: e.target.value})} data-testid="edit-customer-email" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Telefon</label>
                <Input value={editForm.customer_phone || ''} onChange={(e) => setEditForm({...editForm, customer_phone: e.target.value})} data-testid="edit-customer-phone" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Notizen</label>
              <Input value={editForm.notes || ''} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} placeholder="Interne Notizen..." data-testid="edit-notes" />
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] bg-amber-50 p-2 rounded border border-amber-200">
              Der Kunde wird per E-Mail über Änderungen informiert (Standort, Wagen, Zeitraum, Preis).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, booking: null })}>
              Abbrechen
            </Button>
            <Button onClick={saveBookingEdit} disabled={editLoading} data-testid="save-booking-edit">
              {editLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
